import React, { useEffect, useState } from "react";
import { DataGrid, Column, Editing, Lookup } from "devextreme-react/data-grid";
import { Switch } from "devextreme-react/switch";
import { Button } from "devextreme-react/button";
import { TextBox } from "devextreme-react/text-box";
import { LoadPanel } from "devextreme-react/load-panel";
import EmailSettingsApi from "../../api/bulk-email/emailSettingsApi";
import { useMsal } from "@azure/msal-react";
import { Snackbar } from "../../components";

const snackbarType = {
	success: "success",
	error: "error",
};

const EmailSettingsPage = () => {
	const [emailSettings, setEmailSettings] = useState({
		exchangeAccounts: [],
		propertyTaxPath: "",
		primaryExchangeAccount: null,
		lawbaseAdministratorAccounts: null,
	});
	const [dropdownUsers, setDropdownUsers] = useState([]);
	const [availableUsers, setAvailableUsers] = useState([]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [hasChanges, setHasChanges] = useState(false);
	const [editingRowId, setEditingRowId] = useState(0); // The email column is only editable for rows with id === 0
	const [propertyTaxFieldInitialized, setPropertyTaxFieldInitialized] =
		useState(false);
	const [snackbarOptions, setSnackbarOptions] = useState({
		visible: false,
		type: "",
		message: "",
	});
	const { accounts } = useMsal();
	const username = accounts[0]?.name || "Guest";

	useEffect(() => {
		const fetchData = async () => {
			setIsLoading(true);
			try {
				// Use Promise.all to wait for both requests to complete
				const [settingsData, usersData] = await Promise.all([
					EmailSettingsApi.getSettingsAsync(),
					EmailSettingsApi.getAllExchangeAccountsAsync(),
				]);

				// Update email settings
				setEmailSettings({
					exchangeAccounts: settingsData.exchangeAccounts || [],
					propertyTaxPath: settingsData.propertyTaxPath || "",
					primaryExchangeAccount: settingsData.primaryExchangeAccount,
				});

				// Store all users
				setDropdownUsers(usersData);

				// Filter out users that are already in the grid
				const existingGraphIds = (
					settingsData.exchangeAccounts || []
				).map((account) => account.graphId);
				const filteredUsers = usersData.filter(
					(user) => !existingGraphIds.includes(user.id)
				);
				setAvailableUsers(filteredUsers);
			} catch (error) {
				console.error("Failed to load data:", error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchData();
	}, []);

	// Update available users whenever exchangeAccounts changes
	useEffect(() => {
		if (dropdownUsers.length > 0) {
			const existingGraphIds = emailSettings.exchangeAccounts.map(
				(account) => account.graphId
			);
			const filteredUsers = dropdownUsers.filter(
				(user) => !existingGraphIds.includes(user.id)
			);
			setAvailableUsers(filteredUsers);
		}
	}, [emailSettings.exchangeAccounts, dropdownUsers]);

	const handleRowValidating = (e) => {
		const { newData, oldData } = e;

		// Set Id to 0 for new rows, or keep existing Id for updates
		const rowIsNew = e.oldData === undefined || e.oldData.id === 0;
		if (rowIsNew) {
			newData.id = 0; // Set Id to 0 for new rows
		} else {
			newData.id = oldData.id; // Preserve the existing Id for updates
		}

		// Ensure required fields have values
		if (!newData.graphId && rowIsNew) {
			e.errorText = "Email is required.";
			e.isValid = false;
			return;
		}
	};

	const handleSubmit = async () => {
		setIsSubmitting(true);
		try {
			const updatedSettings = await EmailSettingsApi.updateSettingsAsync(
				emailSettings,
				username
			);
			setEmailSettings(updatedSettings);
			setHasChanges(false);
			showNotification(
				"Email Settings updated successfully.",
				snackbarType.success
			);
		} catch (error) {
			showNotification(
				"An error occurred while updating Email Settings.",
				snackbarType.error
			);
			console.error("Error updating email settings:", error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handlePropertyTaxPathChange = (newPath) => {
		setEmailSettings((prev) => ({
			...prev,
			propertyTaxPath: newPath,
		}));

		if (propertyTaxFieldInitialized === false) {
			setPropertyTaxFieldInitialized(true);
		} else {
			setHasChanges(true);
		}
	};

	const handleRowInserted = (e) => {
		setHasChanges(true);

		// Update the DataGrid with the current state after insertion
		// This ensures our availableUsers list will be updated
		setEmailSettings((prev) => ({
			...prev,
			exchangeAccounts: [...prev.exchangeAccounts],
		}));
	};

	const handleRowUpdated = (e) => {
		setHasChanges(true);
	};

	const handleRowRemoved = (e) => {
		setHasChanges(true);

		// Update the DataGrid with the current state after removal
		// This ensures our availableUsers list will be updated
		setEmailSettings((prev) => ({
			...prev,
			exchangeAccounts: [...prev.exchangeAccounts],
		}));
	};

	const handleRunBulkEmail = async () => {
		setIsSubmitting(true);
		try {
			await EmailSettingsApi.runBulkEmailProcessingAsync();
			showNotification(
				"Bulk email process run successfully.",
				snackbarType.success
			);
		} catch {
			showNotification(
				"An error occurred while running the bulk email process.",
				snackbarType.error
			);
		} finally {
			setIsSubmitting(false);
		}
	};

	const showNotification = (message, type) => {
		setSnackbarOptions({
			message,
			type,
			visible: true,
		});
	};

	const onCloseSnackbar = () => {
		setSnackbarOptions({
			...snackbarOptions,
			visible: false,
		});
	};

	return (
		<div>
			<h2 className={"content-block"}>Email Settings</h2>
			<div className="dx-card wide-card">
				<DataGrid
					className={"dx-card wide-card mtb15"}
					dataSource={emailSettings.exchangeAccounts}
					keyExpr="id"
					showBorders={true}
					onRowValidating={handleRowValidating}
					onRowInserted={handleRowInserted}
					onRowUpdated={handleRowUpdated}
					onRowRemoved={handleRowRemoved}
					onEditingStart={(e) => {
						setEditingRowId(e.data.id);
					}}
					onInitNewRow={(e) => {
						setEditingRowId(0);
					}}
					onRowEditCanceling={() => setEditingRowId(null)}
				>
					<Editing
						mode="row"
						allowUpdating={true}
						allowAdding={true}
						allowDeleting={true}
					/>

					{/* Hidden Id column */}
					<Column dataField="id" visible={false} />

					{/* Email column with dropdown for new rows */}
					<Column
						dataField="graphId"
						caption="Email"
						allowEditing={editingRowId === 0}
						calculateDisplayValue={(rowData) => {
							const selectedUser = dropdownUsers.find(
								(user) => user.id === rowData.graphId
							);
							return selectedUser ? selectedUser.label : "";
						}}
						setCellValue={(rowData, value) => {
							rowData.graphId = value;
							// Find the selected user's email
							const selectedUser = dropdownUsers.find(
								(user) => user.id === value
							);
							if (selectedUser) {
								rowData.emailAddress = selectedUser.label;
							}
						}}
					>
						<Lookup
							dataSource={availableUsers}
							valueExpr="id"
							displayExpr="label"
						/>
					</Column>

					<Column
						dataField="isActive"
						caption="Is Active"
						dataType="boolean"
						cellEditorRender={(cellData) => (
							<Switch
								value={cellData.value ?? true}
								onValueChanged={(e) =>
									cellData.setValue(e.value)
								}
							/>
						)}
					/>

					<Column dataField="description" caption="Description" />
				</DataGrid>

				<div style={{ marginTop: 20, marginBottom: 15 }}>
					<TextBox
						className={"dx-card wide-card"}
						value={emailSettings.propertyTaxPath}
						onValueChanged={(e) =>
							handlePropertyTaxPathChange(e.value)
						}
						placeholder="Enter Property Tax Path"
					/>
				</div>

				<div
					style={{
						display: "flex",
						justifyContent: "flex-end",
						padding: "20px",
						borderTop: "1px solid #ddd",
					}}
				>
					<Button
						text="Submit"
						type="success"
						disabled={!hasChanges}
						onClick={handleSubmit}
					/>
				</div>

				<div style={{ paddingBottom: "18px" }}>
					<Button
						className={"content-block bulk-email-button"}
						text="Run Bulk Email Processing"
						disabled={hasChanges}
						onClick={handleRunBulkEmail}
					/>
				</div>
			</div>

			<LoadPanel
				shadingColor="rgba(0,0,0,0.4)"
				visible={isLoading || isSubmitting}
				showIndicator={true}
				shading={true}
				showPane={true}
				position={{ of: ".dx-card" }}
			/>

			<Snackbar
				message={snackbarOptions.message}
				onHiding={onCloseSnackbar}
				of={".content"}
				options={snackbarOptions}
			/>
		</div>
	);
};

export default EmailSettingsPage;
